import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Sparkles, Pencil, Trash2, Eye } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { PlainSelect, Empty } from "./Common";
import ScribeDialog from "./Scribe";

function NoteEditor({ draft, onClose, clientId, appointmentId, onSaved }) {
  const [values, setValues] = useState({});
  const [share, setShare] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (draft) { setValues(draft.values || {}); setShare(!!draft.share_with_patient); } }, [draft]);
  if (!draft) return null;
  const sections = draft.sections || draft.template.sections;
  const save = async () => {
    setBusy(true);
    try {
      const body = { template_id: draft.template_id || draft.template.id, values, share_with_patient: share, ai_generated: !!(draft.ai || draft.ai_generated), appointment_id: draft.appointment_id || appointmentId || null };
      draft.id ? await api.put(`/notes/${draft.id}`, body) : await api.post(`/clients/${clientId}/notes`, body);
      toast.success("Note saved"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="note-editor">
        <DialogHeader><DialogTitle>{draft.template_name || draft.template.name}{(draft.ai || draft.ai_generated) && <span className="ml-2 text-xs rounded-full bg-primary/10 text-primary px-2 py-0.5 align-middle">AI draft – please review</span>}</DialogTitle></DialogHeader>
        {sections.map((s) => (
          <div key={s.key} className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground">{s.label}</label>
            <Textarea data-testid={`note-field-${s.key}`} rows={s.key === "letter" ? 12 : 3} value={values[s.key] || ""} onChange={(e) => setValues({ ...values, [s.key]: e.target.value })} />
          </div>
        ))}
        <label className="flex items-center gap-3 text-sm"><Switch data-testid="note-share-switch" checked={share} onCheckedChange={setShare} />Share this note with the patient (visible in their patient area)</label>
        <Button className="rounded-full" data-testid="note-save-btn" disabled={busy} onClick={save}>Save note</Button>
      </DialogContent>
    </Dialog>
  );
}

export function ClinicalNotes({ client, appointmentId }) {
  const [all, setNotes] = useState([]);
  const notes = appointmentId ? all.filter((n) => n.appointment_id === appointmentId) : all;
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState("osteo_soap");
  const [draft, setDraft] = useState(null);
  const [scribe, setScribe] = useState(false);
  const load = useCallback(() => api.get(`/clients/${client.id}/notes`).then((r) => setNotes(r.data)), [client.id]);
  useEffect(() => { load(); api.get("/note-templates").then((r) => setTemplates(r.data)); }, [load]);
  const remove = async (n) => { if (window.confirm("Delete this note?")) { await api.delete(`/notes/${n.id}`).catch((e) => toast.error(errMsg(e))); load(); } };

  return (
    <div className="space-y-4" data-testid="clinical-notes">
      <div className="flex flex-wrap items-center gap-2">
        <Button className="rounded-full" data-testid="open-scribe-btn" onClick={() => setScribe(true)}><Sparkles className="h-4 w-4 mr-1.5" />AI Scribe</Button>
        <PlainSelect data-testid="note-template-select" className="!w-auto max-w-xs" value={tpl} onChange={(e) => setTpl(e.target.value)}>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </PlainSelect>
        <Button variant="outline" className="rounded-full" data-testid="new-note-btn" disabled={!templates.length} onClick={() => setDraft({ template: templates.find((t) => t.id === tpl), values: {} })}><Plus className="h-4 w-4 mr-1.5" />New note</Button>
      </div>
      {!notes.length && <Empty text={appointmentId ? "No notes for this appointment yet" : "No clinical notes yet"} />}
      {notes.map((n) => (
        <div key={n.id} className="rounded-[var(--radius)] border bg-card p-5 space-y-3" data-testid={`note-${n.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[180px]"><div className="font-semibold">{n.template_name}</div>
              <div className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()} · {n.author_name}</div></div>
            {n.ai_generated && <span className="text-[11px] rounded-full bg-primary/10 text-primary px-2 py-0.5">AI scribe</span>}
            {n.share_with_patient && <span className="text-[11px] rounded-full bg-emerald-500/15 text-emerald-800 px-2 py-0.5 flex items-center gap-1"><Eye className="h-3 w-3" />Shared</span>}
            <Button size="icon" variant="ghost" data-testid={`edit-note-${n.id}`} onClick={() => setDraft(n)}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" data-testid={`delete-note-${n.id}`} onClick={() => remove(n)}><Trash2 className="h-4 w-4" /></Button>
          </div>
          <dl className="space-y-2 text-sm">
            {n.sections.filter((s) => n.values[s.key]).map((s) => (
              <div key={s.key}><dt className="text-xs font-semibold text-muted-foreground">{s.label.split(" – ")[0]}</dt><dd className="whitespace-pre-wrap">{n.values[s.key]}</dd></div>
            ))}
          </dl>
        </div>
      ))}
      <NoteEditor draft={draft} onClose={() => setDraft(null)} clientId={client.id} appointmentId={appointmentId} onSaved={load} />
      <ScribeDialog open={scribe} onOpenChange={setScribe} client={client} templates={templates} onResult={setDraft} />
    </div>
  );
}
